import { useState, type Dispatch } from 'react';
import type { TemplateConfig } from '@zaviago/print-renderer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import { ChevronRight, Loader2, RotateCcw, Upload, X } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { DocumentAction } from './state/documentReducer';
import { DrillCard } from '@/features/storefront/page-editor/components/DrillCard';
import { SubPageHeader } from '@/features/storefront/page-editor/components/SubPageHeader';
import { ToggleRow, OptionRow, SliderRow } from '@/features/storefront/page-editor/components/SettingsRow';

/** Preset workspace backgrounds — stored in public/print-backgrounds/ */
const PRESET_BACKGROUNDS = [
  { label: 'Wildflower Meadow', url: '/print-backgrounds/wildflower-meadow.webp' },
];

interface TemplateSettingsPanelProps {
  config: TemplateConfig;
  onChange: (partial: Partial<TemplateConfig>) => void;
  onSave: () => void;
  onReset: () => void;
  saving: boolean;
  dirty: boolean;
  /** When in edit mode, enables document-level controls (currency, VAT, Buddhist Era, logo) */
  mode?: 'view' | 'edit';
  /** Dispatch for edit-mode document actions */
  editDispatch?: Dispatch<DocumentAction>;
  /** Current document state for edit-mode controls */
  editCurrency?: string;
  editHasTax?: boolean;
  editLogoUrl?: string;
  editBuddhistEra?: boolean;
  /** Control open/close for mobile sheet mode */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CURRENCY_OPTIONS = [
  { value: 'THB', symbol: '฿', label: 'THB (฿)' },
  { value: 'USD', symbol: '$', label: 'USD ($)' },
  { value: 'EUR', symbol: '€', label: 'EUR (€)' },
];

const FONT_LABELS: Record<string, string> = {
  Sarabun: 'Sarabun',
  Inter: 'Inter',
  system: 'System',
};

const WEIGHT_LABELS: Record<number, string> = {
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semibold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
};

type Page = 'main' | 'logo' | 'font' | 'background' | 'document' | 'sections' | 'layout' | 'attached-docs';

export function TemplateSettingsPanel({
  config,
  onChange,
  onSave,
  onReset,
  saving,
  dirty,
  mode = 'view',
  editDispatch,
  editCurrency,
  editHasTax,
  editLogoUrl,
  editBuddhistEra,
  open,
  onOpenChange,
}: TemplateSettingsPanelProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isEdit = mode === 'edit' && !!editDispatch;

  const content = (
    <PanelContent
      config={config}
      onChange={onChange}
      onSave={onSave}
      onReset={onReset}
      saving={saving}
      dirty={dirty}
      isEdit={isEdit}
      editDispatch={editDispatch}
      editCurrency={editCurrency}
      editHasTax={editHasTax}
      editLogoUrl={editLogoUrl}
      editBuddhistEra={editBuddhistEra}
    />
  );

  // Desktop: inline sidebar — glass effect when workspace background is active
  const hasBg = !!config.workspaceBg;
  if (isDesktop) {
    return (
      <div
        className={`flex h-full w-[280px] flex-col rounded-[20px] m-2 ml-0 border border-border/40 shadow-sm overflow-hidden ${
          hasBg ? 'backdrop-blur-xl bg-white/70 dark:bg-black/50' : 'bg-card'
        }`}
      >
        {content}
      </div>
    );
  }

  // Mobile: slide-in sheet
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[300px] p-0">
        <SheetTitle className="sr-only">Customize Template</SheetTitle>
        <div className="flex h-full flex-col">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Main panel content with drill-down navigation ─── */

function PanelContent({
  config,
  onChange,
  onSave,
  onReset,
  saving,
  dirty,
  isEdit,
  editDispatch,
  editCurrency,
  editHasTax,
  editLogoUrl,
  editBuddhistEra,
}: {
  config: TemplateConfig;
  onChange: (partial: Partial<TemplateConfig>) => void;
  onSave: () => void;
  onReset: () => void;
  saving: boolean;
  dirty: boolean;
  isEdit: boolean;
  editDispatch?: Dispatch<DocumentAction>;
  editCurrency?: string;
  editHasTax?: boolean;
  editLogoUrl?: string;
  editBuddhistEra?: boolean;
}) {
  const [page, setPage] = useState<Page>('main');

  const visibleCount = [
    config.showNotes !== false,
    config.showTerms !== false,
    config.showSignatures !== false,
    config.showPaymentInfo !== false,
    config.repeatHeader === true,
    config.repeatFooter === true,
  ].filter(Boolean).length;

  if (page !== 'main') {
    return (
      <>
        <SubPageHeader
          title={
            page === 'logo' ? 'Logo' :
            page === 'font' ? 'Typography' :
            page === 'background' ? 'Workspace Background' :
            page === 'document' ? 'Document Options' :
            page === 'sections' ? 'Sections' :
            page === 'attached-docs' ? 'Attached Documents' :
            'Layout'
          }
          onBack={() => setPage('main')}
        />
        <div className="flex-1 overflow-y-auto [scrollbar-width:none] [overflow-y-auto::-webkit-scrollbar]:hidden p-4 space-y-4">
          {page === 'logo' && <LogoPage config={config} onChange={onChange} editDispatch={editDispatch} editLogoUrl={editLogoUrl} />}
          {page === 'font' && <FontPage config={config} onChange={onChange} />}
          {page === 'background' && <BackgroundPage config={config} onChange={onChange} />}
          {page === 'document' && <DocumentPage editDispatch={editDispatch!} editCurrency={editCurrency} editHasTax={editHasTax} editBuddhistEra={editBuddhistEra} />}
          {page === 'sections' && <SectionsPage config={config} onChange={onChange} />}
          {page === 'layout' && <LayoutPage config={config} onChange={onChange} />}
          {page === 'attached-docs' && <AttachedDocsPage config={config} onChange={onChange} />}
        </div>
      </>
    );
  }

  // ─── Main page ───
  return (
    <>
      {/* Header */}
      <div className="px-4 py-3">
        <h3 className="text-sm font-medium">Customize Template</h3>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto [scrollbar-width:none] [overflow-y-auto::-webkit-scrollbar]:hidden p-4 space-y-3">
        {/* Drill-down cards */}
        {isEdit && (
          <DrillCard
            label="Logo"
            value={
              config.logoMode === 'text'
                ? (config.logoText || 'Text Logo')
                : (editLogoUrl ? 'Image' : 'None')
            }
            preview={
              config.logoMode === 'text' ? (
                <div className="h-10 w-10 rounded-[14px] bg-muted flex items-center justify-center text-sm font-bold">
                  {(config.logoText || 'L')[0]}
                </div>
              ) : editLogoUrl ? (
                <img src={editLogoUrl} alt="Logo" className="h-10 w-10 rounded-[14px] object-contain border border-border" />
              ) : (
                <div className="h-10 w-10 rounded-[14px] bg-muted flex items-center justify-center text-xs text-muted-foreground">
                  Logo
                </div>
              )
            }
            onClick={() => setPage('logo')}
          />
        )}

        <DrillCard
          label="Font"
          value={FONT_LABELS[config.fontFamily || 'Sarabun'] || config.fontFamily || 'Sarabun'}
          onClick={() => setPage('font')}
        />

        {/* Workspace Background — show preview thumbnail if set */}
        {config.workspaceBg ? (
          <button
            onClick={() => setPage('background')}
            className="w-full rounded-[14px] overflow-hidden border border-border active:scale-[0.98] transition-transform"
          >
            <img src={config.workspaceBg} alt="Workspace background" className="w-full h-20 object-cover" />
            <div className="p-3 flex items-center bg-background">
              <div className="flex-1 text-left">
                <div className="text-[11px] text-muted-foreground">Workspace Background</div>
                <div className="text-sm font-medium">Custom</div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </button>
        ) : (
          <DrillCard
            label="Workspace Background"
            value="None"
            onClick={() => setPage('background')}
          />
        )}

        <DrillCard
          label="Layout"
          value={`${config.pagePadding ?? 18}mm padding`}
          onClick={() => setPage('layout')}
        />

        {isEdit && (
          <DrillCard
            label="Document Options"
            value={`${editCurrency || 'THB'}${editHasTax ? ' + VAT 7%' : ''}${editBuddhistEra ? ' + BE' : ''}`}
            onClick={() => setPage('document')}
          />
        )}

        <DrillCard
          label="Sections"
          value={`${visibleCount} visible`}
          onClick={() => setPage('sections')}
        />

        <DrillCard
          label="Attached Documents"
          value={`${config.docUseHeader ? 'Header' : ''}${config.docUseHeader && config.docUseFooter ? ' + ' : ''}${config.docUseFooter ? 'Footer' : ''}${!config.docUseHeader && !config.docUseFooter ? 'Standalone' : ''}`}
          onClick={() => setPage('attached-docs')}
        />

        {/* Inline: Colors */}
        <section className="space-y-2 pt-1">
          <Label className="text-[11px] text-muted-foreground">Colors</Label>
          <div className="grid grid-cols-2 gap-2">
            <ColorSwatch label="Font" value={config.fontColor || '#1A1A1A'} onChange={(v) => onChange({ fontColor: v })} />
            <ColorSwatch label="Background" value={config.backgroundColor || '#FFFFFF'} onChange={(v) => onChange({ backgroundColor: v })} />
          </div>
          <ColorSwatch label="Accent" value={config.accentColor || '#000000'} onChange={(v) => onChange({ accentColor: v })} />
        </section>

        {/* Inline: Logo Size slider */}
        <section className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">Logo Size</Label>
              <span className="text-xs text-muted-foreground">{config.logoSize || 42}px</span>
            </div>
            <Slider
              value={[config.logoSize || 42]}
              onValueChange={([v]) => onChange({ logoSize: v })}
              min={24}
              max={400}
              step={4}
              className="w-full"
            />
          </section>
      </div>

      {/* Footer */}
      <div className="p-3 space-y-2">
        <Button
          onClick={onSave}
          disabled={!dirty || saving}
          className="w-full h-8 text-xs"
          size="sm"
        >
          {saving && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={onReset}
          disabled={saving}
          className="w-full h-8 text-xs text-muted-foreground"
          size="sm"
        >
          <RotateCcw className="mr-1.5 h-3 w-3" />
          Reset to Defaults
        </Button>
      </div>
    </>
  );
}

/* ─── Sub-pages ─── */

function LogoPage({
  config, onChange, editDispatch, editLogoUrl,
}: {
  config: TemplateConfig;
  onChange: (partial: Partial<TemplateConfig>) => void;
  editDispatch?: Dispatch<DocumentAction>;
  editLogoUrl?: string;
}) {
  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex items-center gap-1 rounded-[14px] bg-muted p-0.5">
        <button
          onClick={() => onChange({ logoMode: 'image' })}
          className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
            (config.logoMode || 'image') === 'image'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Image
        </button>
        <button
          onClick={() => onChange({ logoMode: 'text' })}
          className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
            config.logoMode === 'text'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Text
        </button>
      </div>

      {(config.logoMode || 'image') === 'image' ? (
        <label className="block cursor-pointer">
          {editLogoUrl ? (
            <img src={editLogoUrl} alt="Logo" className="h-16 w-16 object-contain rounded-[14px] border border-border" />
          ) : (
            <div className="h-16 w-16 rounded-[14px] border-2 border-dashed border-border flex items-center justify-center text-xs text-muted-foreground hover:border-foreground/30 transition-colors">
              Upload
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file || !editDispatch) return;
              const reader = new FileReader();
              reader.onload = () => { editDispatch({ type: 'SET_LOGO', payload: reader.result as string }); };
              reader.readAsDataURL(file);
            }}
          />
        </label>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={config.logoText || ''}
            onChange={(e) => onChange({ logoText: e.target.value })}
            placeholder="Company Name"
            className="h-10 w-full rounded-[14px] border border-border bg-background px-3 text-sm"
          />
          <OptionRow label="Font">
            <Select value={config.logoTextFont || 'Inter'} onValueChange={(v) => onChange({ logoTextFont: v })}>
              <SelectTrigger className="h-9 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Inter">Inter</SelectItem>
                <SelectItem value="Sarabun">Sarabun</SelectItem>
                <SelectItem value="Georgia">Georgia</SelectItem>
                <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </OptionRow>
          <OptionRow label="Weight">
            <Select value={String(config.logoTextWeight || 700)} onValueChange={(v) => onChange({ logoTextWeight: Number(v) })}>
              <SelectTrigger className="h-9 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(WEIGHT_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </OptionRow>
          <SliderRow
            label="Letter Spacing"
            value={config.logoTextLetterSpacing ?? 0}
            onChange={(v) => onChange({ logoTextLetterSpacing: v })}
            min={-2} max={20} step={0.5}
            suffix="px"
          />
        </div>
      )}

      <SliderRow
        label="Logo Size"
        value={config.logoSize || 42}
        onChange={(v) => onChange({ logoSize: v })}
        min={24} max={400} step={4}
        suffix="px"
      />
    </div>
  );
}

function FontPage({ config, onChange }: { config: TemplateConfig; onChange: (partial: Partial<TemplateConfig>) => void }) {
  return (
    <div className="space-y-4">
      <OptionRow label="Font Family">
        <Select value={config.fontFamily || 'Sarabun'} onValueChange={(v) => onChange({ fontFamily: v as TemplateConfig['fontFamily'] })}>
          <SelectTrigger className="h-9 w-[130px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Sarabun">Sarabun</SelectItem>
            <SelectItem value="Inter">Inter</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>
      </OptionRow>
      <SliderRow
        label="Font Size"
        value={config.fontSize || 10}
        onChange={(v) => onChange({ fontSize: v })}
        min={8} max={14} step={1}
        suffix="pt"
      />
      <SliderRow
        label="Line Height"
        value={config.lineHeight || 1.4}
        onChange={(v) => onChange({ lineHeight: Math.round(v * 10) / 10 })}
        min={1} max={2.4} step={0.1}
      />
    </div>
  );
}

function BackgroundPage({ config, onChange }: { config: TemplateConfig; onChange: (partial: Partial<TemplateConfig>) => void }) {
  return (
    <div className="space-y-4">
      {/* Current + remove */}
      {config.workspaceBg && (
        <div className="relative">
          <img src={config.workspaceBg} alt="Current background" className="w-full h-28 object-cover rounded-[14px] border border-border" />
          <button
            onClick={() => onChange({ workspaceBg: undefined })}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Presets */}
      <div className="space-y-2">
        <Label className="text-[11px] text-muted-foreground">Presets</Label>
        <div className="grid grid-cols-2 gap-2">
          {PRESET_BACKGROUNDS.map((bg) => (
            <button
              key={bg.url}
              onClick={() => onChange({ workspaceBg: bg.url })}
              className={`relative h-20 rounded-[14px] overflow-hidden border-2 transition-colors ${
                config.workspaceBg === bg.url ? 'border-primary' : 'border-border hover:border-foreground/30'
              }`}
            >
              <img src={bg.url} alt={bg.label} className="h-full w-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                <span className="text-[10px] text-white font-medium">{bg.label}</span>
              </div>
            </button>
          ))}
          {/* Upload */}
          <label className="h-20 rounded-[14px] border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-foreground/30 transition-colors cursor-pointer gap-1">
            <Upload className="h-4 w-4" />
            <span className="text-[10px]">Upload</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => { onChange({ workspaceBg: reader.result as string }); };
                reader.readAsDataURL(file);
              }}
            />
          </label>
        </div>
      </div>

      {/* Remove button */}
      {config.workspaceBg && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({ workspaceBg: undefined })}
          className="w-full h-9 text-xs text-muted-foreground"
        >
          Remove Background
        </Button>
      )}
    </div>
  );
}

function LayoutPage({ config, onChange }: { config: TemplateConfig; onChange: (partial: Partial<TemplateConfig>) => void }) {
  return (
    <div className="space-y-4">
      <SliderRow
        label="Page Padding"
        value={config.pagePadding ?? 18}
        onChange={(v) => onChange({ pagePadding: v })}
        min={5} max={30} step={1}
        suffix="mm"
      />
    </div>
  );
}

function AttachedDocsPage({ config, onChange }: { config: TemplateConfig; onChange: (partial: Partial<TemplateConfig>) => void }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Control how attached documents (NDA, SOW, SLA) appear after the quotation.
      </p>
      <ToggleRow label="Use Quotation Header" checked={!!config.docUseHeader} onChange={(v) => onChange({ docUseHeader: v })} />
      <ToggleRow label="Use Quotation Footer" checked={!!config.docUseFooter} onChange={(v) => onChange({ docUseFooter: v })} />
    </div>
  );
}

function DocumentPage({
  editDispatch, editCurrency, editHasTax, editBuddhistEra,
}: {
  editDispatch: Dispatch<DocumentAction>;
  editCurrency?: string;
  editHasTax?: boolean;
  editBuddhistEra?: boolean;
}) {
  return (
    <div className="space-y-4">
      <OptionRow label="Currency">
        <Select
          value={editCurrency || 'THB'}
          onValueChange={(v) => {
            const opt = CURRENCY_OPTIONS.find((c) => c.value === v);
            if (opt) editDispatch({ type: 'SET_CURRENCY', payload: { currency: opt.value, symbol: opt.symbol } });
          }}
        >
          <SelectTrigger className="h-9 w-[120px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CURRENCY_OPTIONS.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </OptionRow>
      <ToggleRow label="VAT 7%" checked={!!editHasTax} onChange={() => editDispatch({ type: 'TOGGLE_VAT' })} />
      <ToggleRow label="Buddhist Era Dates" checked={!!editBuddhistEra} onChange={(v) => editDispatch({ type: 'SET_BUDDHIST_ERA', payload: v })} />
    </div>
  );
}

function SectionsPage({ config, onChange }: { config: TemplateConfig; onChange: (partial: Partial<TemplateConfig>) => void }) {
  return (
    <div className="space-y-3">
      <ToggleRow label="Show Notes" checked={config.showNotes !== false} onChange={(v) => onChange({ showNotes: v })} />
      <ToggleRow label="Show Terms" checked={config.showTerms !== false} onChange={(v) => onChange({ showTerms: v })} />
      <ToggleRow label="Show Signatures" checked={config.showSignatures !== false} onChange={(v) => onChange({ showSignatures: v })} />
      <ToggleRow label="Show Payment Info" checked={config.showPaymentInfo !== false} onChange={(v) => onChange({ showPaymentInfo: v })} />
      <ToggleRow label="Repeat Header" checked={config.repeatHeader === true} onChange={(v) => onChange({ repeatHeader: v })} />
      <ToggleRow label="Repeat Footer" checked={config.repeatFooter === true} onChange={(v) => onChange({ repeatFooter: v })} />
    </div>
  );
}

/* ─── Shared small components (re-exported from page-editor) ─── */

function ColorSwatch({
  label, value, onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 rounded-[14px] border border-border p-2 cursor-pointer hover:bg-muted/50 transition-colors">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 w-6 cursor-pointer rounded-md border border-border p-0.5 shrink-0"
      />
      <div className="min-w-0">
        <div className="text-xs font-medium truncate">{label}</div>
        <div className="text-[10px] text-muted-foreground font-mono uppercase">{value}</div>
      </div>
    </label>
  );
}
